import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ACCENT } from "../artistStudio/shared";
import { fanHomeApi, isSignedIn, apiMessage } from "./fanHomeApi";
import { showError } from "../common/errorToast";
import { TrackCardSkeleton } from "./TrackCard";
import { CARD_GRID } from "./Section";

// Small cards shared by Search and My Library: a playlist, an artist (with Follow), and a "Load more" button.
const Cover = ({ url, round = false, className = "" }) => (
  url
    ? <img src={url} alt="" loading="lazy" className={`w-full h-full object-cover ${className}`} />
    : <div className={`w-full h-full flex items-center justify-center bg-[#d8e4dc] text-gray-400 ${round ? "" : ""}`} aria-hidden="true">
        <svg viewBox="0 0 24 24" className="h-1/3 w-1/3" fill="currentColor"><path d="M9 18V5l12-2v13a3 3 0 1 1-2-2.83V6.3L11 7.5V18a3 3 0 1 1-2-2.83Z" /></svg>
      </div>
);

export function PlaylistCard({ playlist }) {
  return (
    <article className="min-w-0" aria-label={`Playlist ${playlist.name}`}>
      <Link to={`/fan/playlists/${playlist.id}`} className="block aspect-square rounded-2xl overflow-hidden bg-gray-100"><Cover url={playlist.cover_url} /></Link>
      <Link to={`/fan/playlists/${playlist.id}`} className="mt-2 block font-semibold text-gray-900 truncate hover:underline" title={playlist.name}>{playlist.name}</Link>
      <p className="text-sm text-gray-500 truncate">{playlist.track_count} {playlist.track_count === 1 ? "sound" : "sounds"}{playlist.owner_name ? ` · ${playlist.owner_name}` : ""}</p>
    </article>
  );
}

export function ArtistCard({ artist }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [following, setFollowing] = useState(Boolean(artist.following));
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    if (!isSignedIn()) { navigate("/login", { state: { from: location } }); return; }
    if (busy) return;
    setBusy(true);
    try {
      const r = following ? await fanHomeApi.unfollow(artist.slug) : await fanHomeApi.follow(artist.slug);
      setFollowing(Boolean(r?.following));
    } catch (e) {
      showError(apiMessage(e, "We couldn't update this."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <article className="min-w-0 text-center" aria-label={`Artist ${artist.name}`}>
      <Link to={`/artists/${encodeURIComponent(artist.slug)}`} className="mx-auto block aspect-square w-full max-w-[160px] rounded-full overflow-hidden bg-gray-100"><Cover url={artist.picture_url} /></Link>
      <Link to={`/artists/${encodeURIComponent(artist.slug)}`} className="mt-2 block font-semibold text-gray-900 truncate hover:underline" title={artist.name}>{artist.name}</Link>
      <p className="text-xs text-gray-500">{artist.followers_count} {artist.followers_count === 1 ? "follower" : "followers"}</p>
      <button
        type="button" onClick={toggle} disabled={busy} aria-pressed={following}
        className={`mt-2 rounded-full font-semibold px-5 py-1.5 text-sm border-2 disabled:opacity-60 ${following ? "bg-white" : "text-white"}`}
        style={following ? { borderColor: ACCENT, color: ACCENT } : { backgroundColor: ACCENT, borderColor: ACCENT }}
      >{following ? "Following" : "Follow"}</button>
    </article>
  );
}

export function LoadMore({ onClick, busy }) {
  return (
    <div className="mt-6 text-center">
      <button type="button" onClick={onClick} disabled={busy} className="rounded-full border-2 px-6 py-2 text-sm font-semibold disabled:opacity-60" style={{ borderColor: ACCENT, color: ACCENT }}>{busy ? "Loading…" : "Load more"}</button>
    </div>
  );
}

export function GridSkeleton({ count = 4 }) {
  return <div className={CARD_GRID} role="status" aria-label="Loading">{Array.from({ length: count }).map((_, i) => <TrackCardSkeleton key={i} />)}</div>;
}

export function Notice({ children, action }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-gray-50 px-5 py-8 text-center text-gray-600" role="status">
      <div>{children}</div>
      {action}
    </div>
  );
}
